// Shared data adapter for the shoppable-format engines.
//
// Every engine in this folder is driven by REAL in-repo demo data: the
// per-vertical content registry (content/verticals), which itself
// consolidates demo-products + widget-gallery JSON. Nothing here invents a
// product, photo, review, or stat — the engines render only what the
// registry already trusts as on-topic for a given industry.
//
// The mockup this folder reskins used solid-colour swatches + keyword-photo /
// Pexels `kw` media and fabricated stats. We deliberately do NOT borrow any
// of that: imagery comes from the registry's DemoPosts/DemoProducts (real
// curated Unsplash IDs, SafeImage-guarded), proof comes from the registry's
// real `proofPoints` (sourced benchmarks), and a brand-gradient fallback
// covers any slot with no photo so we never present a fabricated photo as a
// real customer's.

import type { DemoPost, DemoProduct } from '@/lib/copy/demo-products'
import { getDemoProduct } from '@/lib/copy/demo-products'
import { getVertical, type Vertical } from '@/content/verticals'

/** A product resolved + flattened for an engine card/rail row. */
export interface FormatProduct {
  id: string
  name: string
  price: string
  brand: string
  tag: string
  blurb: string
  image: string
  rating: number
  reviews: number
}

/** A post (lifestyle image / reel) with its hotspots resolved to products. */
export interface FormatPost {
  image: string
  videoSrc?: string
  user: string
  caption: string
  likes: number
  comments: number
  /** Hotspots keyed to a FormatProduct id, positioned in % of the frame. */
  hotspots: { x: number; y: number; product: FormatProduct }[]
}

/** The full, engine-ready bundle for one industry. */
export interface FormatData {
  slug: string
  name: string
  /** Short, scannable industry label (the registry name, trimmed). */
  shortName: string
  /** Real, sourced proof points from the vertical registry. */
  proof: { stat: string; label: string }[]
  /** Real per-vertical hero image when the registry has one, else undefined. */
  heroImage?: string
  posts: FormatPost[]
  products: FormatProduct[]
}

// Deterministic per-vertical hue so the brand-gradient fallback (used only
// when a slot has no real photo) is stable and on-brand cobalt-adjacent. No
// product is ever shown as a flat swatch when a real image exists; this is
// purely a backdrop behind copy when imagery is genuinely absent.
function hueForSlug(slug: string): number {
  let h = 0
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) % 360
  return h
}

/** A tasteful brand-gradient backdrop (cobalt-anchored) for image-less slots. */
export function brandGradientFor(slug: string, seed = 0): string {
  const base = hueForSlug(slug)
  const a = (base + seed * 24) % 360
  const b = (a + 40) % 360
  // Anchored toward cobalt by keeping saturation moderate + lightness mid so
  // it reads as a brand surface, never a fake photo.
  return `linear-gradient(135deg, hsl(${a} 42% 32%), hsl(${b} 38% 22%))`
}

function flattenProduct(id: string, p: DemoProduct): FormatProduct {
  return {
    id,
    name: p.name,
    price: p.price,
    brand: p.brand,
    tag: p.options?.[0]?.label ?? p.brand,
    blurb: p.blurb,
    image: p.image,
    rating: p.rating,
    reviews: p.reviews,
  }
}

/**
 * Build the engine-ready bundle for an industry slug from the REAL vertical
 * registry. Returns null when the slug is unknown so callers can skip it.
 */
export function formatDataFor(slug: string): FormatData | null {
  const vertical: Vertical | undefined = getVertical(slug)
  if (!vertical) return null

  const sampleProducts = vertical.media.sampleProducts
  const resolve = (id: string): DemoProduct | null =>
    sampleProducts?.[id] ?? getDemoProduct(id)

  const productMap = new Map<string, FormatProduct>()
  const posts: FormatPost[] = []

  for (const post of vertical.media.reels as DemoPost[]) {
    const hotspots: FormatPost['hotspots'] = []
    for (const h of post.hotspots) {
      const dp = resolve(h.productId)
      if (!dp) continue
      const fp = flattenProduct(h.productId, dp)
      productMap.set(fp.id, fp)
      hotspots.push({ x: h.x, y: h.y, product: fp })
    }
    if (hotspots.length === 0) continue
    posts.push({
      image: post.image,
      videoSrc: post.videoSrc,
      user: post.user,
      caption: post.caption,
      likes: post.likes,
      comments: post.comments,
      hotspots,
    })
  }

  const products = Array.from(productMap.values())

  return {
    slug: vertical.slug,
    name: vertical.name,
    shortName: vertical.name.split('+')[0].split('and')[0].trim(),
    proof: vertical.proofPoints.map((p) => ({ stat: p.stat, label: p.label })),
    heroImage: vertical.media.heroImage,
    posts,
    products,
  }
}

/** Format the star row for a rating (0-5), returns filled/empty counts. */
export function starsFor(rating: number): { full: number; half: boolean } {
  const full = Math.floor(rating)
  return { full, half: rating - full >= 0.5 }
}
